export const profile = {
  extends: 'ContentPage',
  // TWO VIEWS of one page (member-profile stage 3, memberProfileAccess.js
  // memberProfileView): /settings is the person's own settings, every other
  // route a member's profile. The settings tab reads "Settings / <brand>"; it
  // read "<name> / People / <brand>", the profile's title (live 2026-10-28).
  metadata: (el, s) => {
    if (el.call('memberProfileView', s) === 'settings') {
      return { title: el.call('pageTitle', 'account.profile.settingsTitle') }
    }
    const person = el.call('findMe', s)
    // Until the person loads, the page's own word in the active language. A
    // fixed English 'Profile' here read "Profile / ადამიანები / Symbols ჩატი"
    // on a cold Georgian /profile (dev.comm 2026-10-30).
    const name = person?.name || el.call('polyglot', 'account.page.userProfile.title')
    const section = el.call('polyglot', 'account.profile.section')
    return { title: name + ' / ' + section + ' / ' + el.call('brandName') }
  },
  // `eyebrow` is a CUSTOM prop, so a function value is auto-invoked (Rule 70)
  // and ContentPage's `el.parent.parent.parent.eyebrow` reads the resolved
  // string.
  //
  // There is NO `title:` prop any more. `title` is a global HTML attribute
  // that ContentPage read as CONTENT — the one case with no working rewrite
  // form (docs/I18N-CONVENTION.md §1.2), which is why the heading sat in
  // English long after the rest of the page was extracted. Overriding
  // ContentPage's Title SLOT (PageMain.Header, bottom of this file) solves it
  // without a ContentPage change, and dropping the prop also drops the stray
  // native tooltip it put on the page element. ContentPage's Title reads
  // `…parent.title || ''` and this page supplies its own `text:`, so nothing
  // reads the removed prop.
  eyebrow: (el) => el.call('polyglot', 'account.profile.eyebrow'),
  // Every entry carries TWO strings, and they are not the same thing.
  //
  // `sectionKey` is the SCROLL KEY. ContentPageSectionLink matches it against
  // `[data-content-section="<key>"]` in MemberProfile.js and writes it to
  // `root.contentSection`. It is the English string those attributes already
  // carry, and it must NEVER be translated — a translated key breaks the
  // scroll on the first switch, and breaks any link that names a section.
  // MemberProfile.js therefore needs no edit: its attribute stops being a
  // label and becomes an opaque key.
  //
  // `label` is the VISIBLE string, and it goes through polyglot.
  // `el.call('polyglot', key)` — the FUNCTION form, not a `{{ … | polyglot }}`
  // template. Only `text:` gets a reactive template effect; a template on an
  // ATTRIBUTE is resolved once in the static attribute pass, which overwrites
  // the prop, so it freezes in the boot language. The function form reads
  // `root.lang` through `getActiveLang`, and this whole factory runs inside
  // ContentPage's reactive `children` factory, so the list re-renders on a
  // switch. Polyglot is registered on every app that mounts this page:
  // `context.polyglot` lives in this package's own context.js and every
  // consumer (workspace, canvas, marketplace, preview, analytics) inherits it
  // wholesale through mergeOneLibrary's copy-whole-key branch.
  //
  // The keys live in i18n/{en,ka}/account.json and are parity-checked by
  // components/LanguageSelectField.test.js. Add a section => add the key to
  // BOTH bundles.
  //
  // The list itself is ONE shared function since stage 3
  // (memberProfileAccess.js memberProfileSections): this page and the shell's
  // /my ask it, so each rail lists exactly the cards its view shows.
  sections: (el, s) => el.call('memberProfileSections', s),

  // Content mounts in ContentPage's main column (PageMain.Body) — the
  // notes-page skeleton; see components/ContentPage.js.
  PageMain: {
    // The heading. FUNCTION form, not a `{{ … | polyglot }}` template: a
    // `text:` on a leaf switches under both forms, but this whole path is
    // held to one form by components/LanguageSelectField.test.js — templates
    // freeze on an ATTRIBUTE, and one file mixing both invites the wrong one
    // being copied onto an attribute next.
    Header: {
      LeftGroup: {
        // The heading names the view: Settings on /settings, Profile
        // elsewhere. One title per host (design-direction.md §3): under a
        // pane bar the settings heading is the bar word for word, so it
        // yields there; the profile heading says more than the bar (the
        // person's name), so it stays as the pane's `h2`.
        Title: {
          hide: (el, s) => {
            const view = el.call('memberProfileView', s)
            const underBar = !!el.call('paneTitleBarShown', s)
            return view === 'settings' && underBar
          },
          text: (el, s) =>
            el.call(
              'polyglot',
              el.call('memberProfileView', s) === 'settings'
                ? 'account.profile.settingsTitle'
                : 'account.profile.title'
            )
        }
      }
    },
    Body: {
      PageBody: {
        flow: 'y',
        gap: 'D',
        flex: '1',
        minWidth: '0',
        // FULL WIDTH (2026-09-03 per Nika "content layout adapt to layout -
        // make it full width"). The old `maxWidth: 'I'` cap stranded a 799px
        // column on the left of a 1649px body — two thirds of the page was
        // empty. MemberProfile now flows its cards in two stacks per view
        // (a wide main stack beside a narrow side stack), so no row is
        // wider than its stack.
        width: '100%',
        MemberProfile: {
          // Mail (architecture/MAIL.md §3.4) — the account card ships with
          // @symbo-ls/workspace-mail and is mounted HERE because this page is
          // the account surface every consumer reaches at /settings (and
          // canvas's /settings). It sits at the END OF THE SETTINGS MAIN STACK,
          // after Notifications (stage 3): it mounted as a loose root item
          // after both settings stacks, on a line of its own.
          //
          // ONE GATE with its rail row (memberProfileAccess.js
          // showsMailSettings): the component is registered (canvas links no
          // mail package), it is your own page, and the `mail` feature flag is
          // on for this workspace (`navItemVisible`, never a hand-rolled flag
          // read). The stack itself shows on /settings only.
          //
          // ⚠️ NO `subscribeTo` HERE. DOMQL assigns an ARRAY wholesale rather
          // than merging it, so a mount-site `subscribeTo: ['_featureFlags',
          // 'workspace']` DELETED the card's own four keys (mailAccounts,
          // mailSetup, mailSettingsDraft, mailSettingsBusy) — the card would
          // have stopped re-rendering on every write its own data plane
          // makes. `ifDeps` is what this gate actually needs and it is
          // narrower: the framework reads the declared selectors inside the
          // `if` effect itself, so the flag flip re-runs THIS gate without
          // touching the component's own subscriptions.
          SettingsMain: {
            MailAccountSettingsCard: {
              ifDeps: [
                (el, s) => s?.root && s.root._featureFlags,
                (el, s) => s?.root && s.root.workspace
              ],
              if: (el, s) => !!el.call('showsMailSettings', s)
            }
          }
        }
      }
    }
  }
}
